import * as React from 'react'
import { useQueryTimesheetBasicSettings } from '@/lib/api/company/configuration/timesheet-basic/useQueryTimesheetBasicSettings'
import { useMutationTimesheetBasicSettings } from '@/lib/api/company/configuration/timesheet-basic/useMutationTimesheetBasicSettings'
import { useQueryTimesheetAdditionalSettings } from '@/lib/api/company/configuration/timesheet-additional/useQueryTimesheetAdditionalSettings'
import { useMutationTimesheetAdditionalSettings } from '@/lib/api/company/configuration/timesheet-additional/useMutationTimesheetAdditionalSettings'
import type { AppliedSettingModel } from '@/app/models/timesheetModel'
import {
  getFieldSpec,
  getFieldEditability,
  toCamelKey,
  TIME_COMPOSITE_KEYS,
} from './explainSettingFields'

export type EditableValue = boolean | number | string

interface ExplainEditableSettingsOptions {
  companyId: string
  enabled: boolean
}

export interface UseExplainEditableSettingsResult {
  /** Read the value to display for a given backend setting key. */
  getValue: (settingKey: string, fallback: AppliedSettingModel | undefined) => EditableValue
  /** Stage a local change. */
  setValue: (settingKey: string, next: EditableValue) => void
  /** Reset all staged changes. */
  reset: () => void
  /** Persist staged changes by routing each to the right page mutation. */
  save: () => Promise<void>
  /** Are there any staged changes? */
  isDirty: boolean
  /** Mutation in flight. */
  isSaving: boolean
  /** Loading initial settings data. */
  isLoading: boolean
  /**
   * Read-only suffix the settings page shows next to a composite picker
   * (e.g. " - 05:00:00" for night-work end). Returns null when not applicable
   * or when the cached config hasn't loaded yet.
   */
  getReadonlySuffix: (settingKey: string) => string | null
}

const OVERHOURS_LIMIT_MAX = 416

export const useExplainEditableSettings = ({
  companyId,
  enabled,
}: ExplainEditableSettingsOptions): UseExplainEditableSettingsResult => {
  const basicQuery = useQueryTimesheetBasicSettings(enabled ? companyId : '')
  const additionalQuery = useQueryTimesheetAdditionalSettings(enabled ? companyId : '')
  const basicMutation = useMutationTimesheetBasicSettings(companyId)
  const additionalMutation = useMutationTimesheetAdditionalSettings(companyId)

  // Staged changes keyed by backend setting key (PascalCase, e.g. "FitWorkingDays").
  const [overlay, setOverlay] = React.useState<Record<string, EditableValue>>({})

  const reset = React.useCallback(() => {
    setOverlay({})
  }, [])

  const setValue = React.useCallback(
    (settingKey: string, next: EditableValue) => {
      setOverlay((prev) => ({ ...prev, [settingKey]: next }))
    },
    [],
  )

  const getValue = React.useCallback(
    (settingKey: string, fallback: AppliedSettingModel | undefined): EditableValue => {
      if (settingKey in overlay) return overlay[settingKey]
      return readFromSource(
        settingKey,
        fallback,
        basicQuery.data,
        additionalQuery.data,
      )
    },
    [overlay, basicQuery.data, additionalQuery.data],
  )

  const save = React.useCallback(async () => {
    const dirty = Object.entries(overlay)
    if (dirty.length === 0) return

    const basicChanges: Array<[string, EditableValue]> = []
    const additionalChanges: Array<[string, EditableValue]> = []

    for (const [key, value] of dirty) {
      const spec = getFieldSpec(key)
      if (spec.page === 'timesheet-basic') basicChanges.push([key, value])
      else if (spec.page === 'timesheet-additional') additionalChanges.push([key, value])
    }

    const tasks: Promise<unknown>[] = []

    if (basicChanges.length > 0 && basicQuery.data) {
      const next = { ...basicQuery.data }
      for (const [key, value] of basicChanges) {
        applyFieldToBasicDto(key, value, next)
      }
      tasks.push(basicMutation.mutateAsync(next))
    }

    if (additionalChanges.length > 0 && additionalQuery.data) {
      const next = { ...additionalQuery.data }
      for (const [key, value] of additionalChanges) {
        applyFieldToAdditionalDto(key, value, next)
      }
      tasks.push(additionalMutation.mutateAsync(next))
    }

    await Promise.all(tasks)
    setOverlay({})
  }, [overlay, basicQuery.data, additionalQuery.data, basicMutation, additionalMutation])

  const getReadonlySuffix = React.useCallback(
    (settingKey: string): string | null => {
      const data = basicQuery.data
      if (!data) return null
      // Mirrors the settings page's TripleTimeLikeFields "readonly" slot —
      // shows the derived end-time (start + fixed window) so users see what
      // window the night/limit setting actually creates.
      if (settingKey === 'NightTimeStartHour') {
        return (data as any).nightTimeEndHour ?? null
      }
      if (settingKey === 'WorkTimeLimitIntervalStartTime') {
        return (data as any).workTimeLimitIntervalEndTime ?? null
      }
      return null
    },
    [basicQuery.data],
  )

  return {
    getValue,
    setValue,
    reset,
    save,
    isDirty: Object.keys(overlay).length > 0,
    isSaving: basicMutation.isPending || additionalMutation.isPending,
    isLoading: basicQuery.isLoading || additionalQuery.isLoading,
    getReadonlySuffix,
  }
}

// Read the saved (or in-flight) value from the right source. Order:
//   1. The cached settings query (authoritative if loaded — that's what the
//      settings page sees).
//   2. The AppliedSetting from the timesheet response (snapshot at calc time).
//   3. A type-aware empty value.
const readFromSource = (
  settingKey: string,
  applied: AppliedSettingModel | undefined,
  basic: any,
  additional: any,
): EditableValue => {
  const spec = getFieldSpec(settingKey)
  const dtoField = toCamelKey(settingKey)
  const editability = getFieldEditability(settingKey, applied?.type)

  if (spec.page === 'timesheet-basic' && basic) {
    if (editability === 'editable-input-hours') {
      return timeSpanToHours(basic[dtoField] ?? '')
    }
    if (editability === 'editable-time-composite') {
      return readTimeComposite(settingKey, basic, applied)
    }
    if (dtoField in basic) return basic[dtoField]
  }
  if (spec.page === 'timesheet-additional' && additional && dtoField in additional) {
    return additional[dtoField]
  }

  if (!applied) {
    if (editability === 'editable-bool') return false
    if (editability === 'editable-input-hours') return 0
    if (editability === 'editable-time-composite') return '00:00'
    return ''
  }
  if (applied.type === 'bool') return applied.value === 'true'
  if (applied.type?.startsWith('enum:')) {
    const n = parseInt(applied.value, 10)
    return Number.isNaN(n) ? applied.value : n
  }
  if (editability === 'editable-input-hours') {
    return timeSpanToHours(applied.value)
  }
  if (editability === 'editable-time-composite') {
    return timeSpanToHHMM(applied.value)
  }
  return applied.value
}

// "06:00:00" or "0.06:00:00" → "06:00".
const timeSpanToHHMM = (raw: string): string => {
  if (!raw) return '00:00'
  const match = /^(?:(\d+)\.)?(\d{1,2}):(\d{2})/.exec(raw)
  if (!match) return '00:00'
  const hours = parseInt(match[2], 10)
  const minutes = parseInt(match[3], 10)
  return `${pad2(hours)}:${pad2(minutes)}`
}

const pad2 = (n: number) => String(n).padStart(2, '0')

// For composite time fields, reconstitute "HH:MM" from whatever DTO field(s)
// the settings page splits the value into.
const readTimeComposite = (
  settingKey: string,
  basic: any,
  applied: AppliedSettingModel | undefined,
): string => {
  if (settingKey === 'HolidayStartTime') {
    const raw = basic.holidayStartTime ?? '00:00'
    return raw.length >= 5 ? raw.slice(0, 5) : '00:00'
  }
  if (settingKey === 'NightTimeStartHour') {
    return `${pad2(basic.nightTimeStartHours ?? 22)}:${pad2(basic.nightTimeStartMinutes ?? 0)}`
  }
  if (settingKey === 'WorkTimeLimitIntervalStartTime') {
    return `${pad2(basic.workTimeLimitIntervalStartTimeHours ?? 0)}:${pad2(basic.workTimeLimitIntervalStartTimeMinutes ?? 0)}`
  }
  return applied ? timeSpanToHHMM(applied.value) : '00:00'
}

const writeTimeComposite = (settingKey: string, value: string, basic: any) => {
  const [hStr, mStr] = (value || '00:00').split(':')
  const hours = Math.max(0, parseInt(hStr, 10) || 0)
  const minutes = Math.max(0, parseInt(mStr, 10) || 0)
  if (settingKey === 'HolidayStartTime') {
    basic.holidayStartTime = `${pad2(hours)}:${pad2(minutes)}`
    return
  }
  if (settingKey === 'NightTimeStartHour') {
    basic.nightTimeStartHours = hours
    basic.nightTimeStartMinutes = minutes
    return
  }
  if (settingKey === 'WorkTimeLimitIntervalStartTime') {
    basic.workTimeLimitIntervalStartTimeHours = hours
    basic.workTimeLimitIntervalStartTimeMinutes = minutes
    return
  }
}

// "16.16:00:00" → 16*24 + 16 = 400; "0:00:00" → 0.
const timeSpanToHours = (raw: string): number => {
  if (!raw) return 0
  const match = /^(?:(\d+)\.)?(\d{1,2}):(\d{2}):(\d{2})/.exec(raw)
  if (!match) return 0
  const days = match[1] ? parseInt(match[1], 10) : 0
  const hours = parseInt(match[2], 10)
  return days * 24 + hours
}

// 400 → "16.16:00:00" (mirrors the settings page's encoding).
const hoursToTimeSpan = (hours: number): string => {
  const clamped = Math.min(OVERHOURS_LIMIT_MAX, Math.max(0, Math.floor(hours)))
  const days = Math.floor(clamped / 24)
  const remHours = clamped % 24
  return `${days}.${String(remHours).padStart(2, '0')}:00:00`
}

// Mutates `dto` in place; returns nothing.
const applyFieldToBasicDto = (
  settingKey: string,
  value: EditableValue,
  dto: any,
): void => {
  if (settingKey === 'OverhoursLimit') {
    dto.overhoursLimit = hoursToTimeSpan(typeof value === 'number' ? value : Number(value))
    return
  }
  if (TIME_COMPOSITE_KEYS.has(settingKey)) {
    writeTimeComposite(settingKey, String(value), dto)
    return
  }
  const dtoField = toCamelKey(settingKey)
  dto[dtoField] = value
}

const applyFieldToAdditionalDto = (
  settingKey: string,
  value: EditableValue,
  dto: any,
): void => {
  const dtoField = toCamelKey(settingKey)
  dto[dtoField] = value
}
